// shared/products/performance/config/visitForms.ts
import type { VisitForm } from "./schema";

/**
 * E5 R1: the packs' visit forms. Senior living's is the client's Regional Director visit as the
 * wireframe carries it (`wireframe/data.js:848-849`: 17 sections, 172 items, 12 financial blocks);
 * the base pack's is a short neutral location review. HR configures every part in Settings.
 * Generated from the wireframe's data; edit through Settings, not here.
 */
export const SENIOR_LIVING_VISIT_FORM: VisitForm = {
  key: "regional_director_visit",
  name: "Regional Director visit",
  sections: [
    {
      key: "ir",
      title: "Incident Reporting (IR)",
      group: "Opening",
      items: [
        "ED understands reporting requirements, methods, and timeline?",
        "IR guideline posted for associates to know who to contact in certain IRs?",
        "ED understands regulatory reporting requirements and timeline?",
        "Incidents reported within 24 hours of occurrence to RDO within last 30 days?",
        "Incident reports completed in full and timely?",
        "Total Incidents Reported Last Quarter",
        "Total Closed?",
        "Reviewed Concern/Grievance Log. Resolutions documented?",
        "Incident reports reviewed during Safety Meetings?",
      ],
    },
    {
      key: "cdd",
      title: "Clinical Denial & Discharge (CDD)",
      group: "Opening",
      items: [
        "Reviewed Clinical Denial & Discharge Policy with CRD/ED/DON?",
        "# of Clinical Denials last quarter?",
      ],
    },
    {
      key: "standup",
      title: "Stand-Up",
      group: "Opening",
      items: [
        "Sales team discussed move-ins, tour plans, etc... (Stand-up Board)?",
        "New Resident Move-Ins reviewed?",
        "24 Hour Communication Log reviewed by DON?",
        "Incident/Accident Reports reviewed?",
        "At Risk Residents discussed and plan is in place?",
        "Prior weeks (30 Days) Stand-up minutes & boards available and complete?",
        "Employee recognitions shared?",
      ],
    },
    {
      key: "impressions",
      title: "Impressions!",
      group: "Standards",
      items: [
        "Drive-by/external appearance is clean and well manicured?",
        "100% employees compliant with dress code (name badge, etc...)?",
        "Lobby is set to standard, clean, warm, pleasant odor and inviting?",
        "Front entrance/vestibule is inviting and free of clutter? (No DME, Packages, etc.)",
        "Concierge greets visitors warmly, authentically and timely?",
        "Residents in lobby/common areas groomed, engaged and active?",
        "Concierge has visitor and resident logs in use?",
        "Concierge answers phone correctly and timely?",
        "Dining room clean, organized and set to standard?",
        "Concierge desk is neat, organized, free of clutter and personal items?",
        "Laundry room clean and chemicals secured?",
        "Activity rooms clean, decluttered and orderly?",
        "Nurses stations clean, neat, free of clutter and personal items?",
        "Public restrooms clean and supplied?",
        "Offices clean and orderly?",
        "Hallways, nooks, sitting areas clean and orderly?",
        "Maint/Mech/Storage rooms clean and organized?",
        "Bistro clean, orderly and supplied?",
        "Discovery Room clean and set to standard?",
        "Public areas 100% free of taped/tacked items?",
        "Memory care area clean, organized and set to standard?",
        "100% vacant units rent ready?",
        "Employee breakroom is clean and in good condition?",
        "Employee breakroom has labor posters?",
        "Model room is clean and set to standard?",
        "Employees are friendly, interactive, good morale?",
        "Employee recognition board in use and contains current month cards?",
        "Employees know our Mission Statement? (ask 3)",
      ],
    },
    {
      key: "admin",
      title: "Administrative",
      group: "Standards",
      items: [
        "Employee compliance training completed timely and to standard?",
        "Dementia training completed timely and to regulation?",
        "Review last quarter's staff evaluations and increases? (3)",
        "Last 3 hires have TB test completed timely if applicable?",
        "Last 5 new hires have completed orientation & documentation?",
        "LTC insurance monthly process in place and timely?",
        "Recruitment platform effectively and timely used?",
        "Delinquent accounts in process per protocol?",
        "Nurse Call Response Times discussed and acceptable?",
        "Priorities/Plans from prior month variance implemented/accomplished?",
        "Review Prior Months financial variance analysis?",
        "Prior month credit card activity was necessary and non-routine?",
        "Understands AP process, role and timelines? (PO Box, ISTA, etc.)",
        "Review Rent Roll: Free of errors, charges correct?",
        "Last 5 MIs paperwork complete and full funds collected?",
        "Reviewed AR aging report, collection efforts and standards?",
        "Ancillary charges and trended revenues are accurate?",
        "Community AP processing times within standard?",
      ],
    },
    {
      key: "engagement",
      title: "Resident Engagement",
      group: "Standards",
      items: [
        "Life Enrichment program is in use and to standard?",
        "Activities are occurring as the calendar indicates at the posted times?",
        "Residents are engaged, not clustered with nothing to do?",
        "LED and MCD offices are tidy, clean and organized if supplies are in their offices?",
        "Memory Care dining experience meets standards?",
        "Creativity boxes in use, available, and stocked? (15+)",
        "Staff are actively engaged throughout the day with programming?",
        "Activity/Creativity boxes are readily available for resident use?",
      ],
    },
    {
      key: "maintenance",
      title: "Maintenance",
      group: "Standards",
      items: [
        "State required inspections completed/scheduled timely?",
        "Preventative maintenance items on-track and up-to-date? (Review TELS)",
        "Reviewed last 3 fire drills?",
        "Reviewed last elopement drill and disaster drill?",
        "Chemicals labeled and stored appropriately? (Locked areas)",
        "SDS Binders updated and placed appropriately?",
        "Reviewed vendor contracting protocol, vendor approval, etc.?",
        "Reviewed work order completion process, tracking and outcomes?",
        "HSK carts locked and stored when not in use?",
        "HSK/MTA task sheets accurate, detailed and time oriented?",
        "Touch-up painting schedule/routine in place?",
        "Reviewed room turn timeline and procedures?",
        "Reviewed floor maintenance expectations/contract?",
        "Review Fleet Safety Manual and Maintenance?",
        "Reviewed Cap-X requests, process, and forms?",
        "Reviewed expenses, budget adherence and priorities?",
      ],
    },
    {
      key: "culinary",
      title: "Culinary Services",
      group: "Standards",
      items: [
        "Dining room set-up, clean and odor free?",
        "Tables set-up, neat, orderly and consistent per policy?",
        "Current and accurate menus posted and followed?",
        "Week At A Glance Menu posted/distributed?",
        "Daily menus are in place on each table in the dining room.",
        "Food orders taken timely, professionally and accurately?",
        "Drink and meal service was timely, presents well and professional?",
        "Tables turned timely, orderly and professionally?",
        "Plate presentation was professional?",
        "Portion sizes appropriate?",
        "Food was appropriate temperature and tasty?",
        "Resident's assisted when necessary/requested?",
        "Was the planned menu actually served? (Items available?)",
        "Special Diet binder/board/process in place and in use?",
        "Dining room tables, table legs and chairs clean without build-up?",
        "Table condiments & caddy clean and appropriate for meal?",
        "Food temperature logs used and omission free? (MC & AL)",
        "Fridge/freezer/dishwasher temp logs used and omission free?",
        "Fridges/freezer organized correctly and free of debris?",
        "Food dated and labeled? (fridge, freezer and storage)",
        "Monthly food committee notes are completed?",
        "Kitchen sanitation schedule/task sheet is accurate, in use, and effective?",
        "Par levels sufficient for china, glasses, utensils?",
        "Substitution log is in use and substitutions are documented appropriately?",
        "Kitchen walls are clean and free of splatter?",
        "Kitchen floor is clean without build-up?",
        "Proper process for vendor contracts and vendor approvals are followed?",
        "Equipment/supplies in good working order?",
      ],
    },
    {
      key: "salesocc",
      title: "Sales & Occupancy",
      group: "Operations Review",
      items: [
        "Current occupancy vs. budget",
        "Move-ins / move-outs MTD and YTD",
        "Review upcoming 30-day move-in pipeline",
        "Review leads, tours, deposits and conversions",
        "Review reasons for lost leads",
        "Review reasons for resident move-outs",
        "Tour available apartments; confirm market-ready",
      ],
    },
    {
      key: "clinicalops",
      title: "Clinical Operations",
      group: "Operations Review",
      items: [
        "Review falls, injuries and trends",
        "Review hospital/ER transfers",
        "Review medication errors/variances",
        "Review infections/outbreaks",
        "Review residents with significant condition changes",
        "Confirm assessments/service plans are current",
        "Review high-risk residents",
        "Review outstanding clinical follow-up items",
      ],
    },
    {
      key: "staffing",
      title: "Staffing & Labor",
      group: "Operations Review",
      items: [
        "Review open positions",
        "Review staffing schedule vs. census/acuity",
        "Review overtime and agency usage",
        "Review call-offs and attendance trends",
        "Review turnover and retention",
        "Confirm required training is current",
        "Meet with department heads",
      ],
    },
    {
      key: "resexp",
      title: "Resident Experience",
      group: "Operations Review",
      items: [
        "Speak with residents during visit",
        "Observe staff/resident interactions",
        "Review resident/family complaints",
        "Review satisfaction results and action plans",
        "Observe activities and resident engagement",
        "Review dining experience during a meal period",
      ],
    },
    {
      key: "dining",
      title: "Dining",
      group: "Operations Review",
      items: [
        "Observe food quality, presentation and temperature",
        "Inspect kitchen cleanliness and sanitation",
        "Review food cost vs. budget",
        "Verify menus are being followed",
        "Review dining complaints and follow-up",
      ],
    },
    {
      key: "physplant",
      title: "Maintenance / Physical Plant",
      group: "Operations Review",
      items: [
        "Complete exterior walk-around",
        "Tour common areas and resident corridors",
        "Review cleanliness, odors and overall appearance",
        "Inspect vacant apartments",
        "Review open work orders",
        "Review average time to close work orders",
        "Review preventive maintenance completion",
        "Review outstanding capital needs",
        "Check life-safety equipment/inspection status",
      ],
    },
    {
      key: "housekeeping",
      title: "Housekeeping",
      group: "Operations Review",
      items: [
        "Inspect common areas and restrooms",
        "Inspect sample resident apartments",
        "Review housekeeping staffing and schedules",
        "Review deep-clean/floor-care schedule",
      ],
    },
    {
      key: "regulatory",
      title: "Regulatory / Compliance",
      group: "Operations Review",
      items: [
        "Review open survey deficiencies/POCs",
        "Review reportable incidents",
        "Review licensing/inspection requirements",
        "Verify required postings/documentation",
        "Review outstanding compliance issues",
      ],
    },
    {
      key: "leadership",
      title: "Leadership",
      group: "Operations Review",
      items: [
        "Review ED's top 3 priorities",
        "Review department-head performance",
        "Review outstanding action plans",
        "Identify leadership/accountability concerns",
        "Recognize strong performance/wins",
      ],
    },
  ],
  financial: [
    {
      key: "fin_previsit",
      title: "Pre-Visit Financial Review",
      type: "table",
      hint: "RDO + ED complete together — accurate, current numbers",
      rows: [
        "MTD / YTD NOI vs. budget",
        "Revenue variances (vs. budget)",
        "Major expense variances",
        "Labor vs. budget — overtime & agency",
        "Controllable expenses",
        "NOI improvement actions in progress",
      ],
    },
    {
      key: "fin_scan",
      title: "Pre-Call Scan",
      type: "scan",
      hint: "walk in already knowing",
      items: [
        "Pulled live SkyPoint dashboards",
        "Pulled census & move-in/out trend",
        "Reviewed labor hours, OT %, agency use",
        "Checked last night's incidents / falls",
        "Looked at budget vs. actual (earnings)",
        "Reviewed open AR / collections",
        "Scanned complaints & work orders",
        "Confirmed survey-readiness / open POC items",
        "Reviewed each department's status",
        "Re-read last call's action items",
      ],
    },
    {
      key: "fin_numbers",
      title: "The Numbers",
      type: "metrics",
      hint: "know normal so the abnormal jumps out",
      rows: [
        "Occupancy / census %",
        "Move-ins / move-outs (net)",
        "Labor hours & overtime %",
        "Agency usage ($ / hrs)",
        "Earnings vs. budget",
        "AR / collections aging",
        "Incidents / falls / transfers",
        "Complaints / grievances",
        "Open work orders / life-safety",
      ],
    },
    {
      key: "fin_depts",
      title: "Department-by-Department Review",
      type: "table",
      hint: "the department line — direction down, accountability up",
      rows: ["Clinical", "Dining", "Life Enrichment", "Marketing", "Maintenance", "Memory Care"],
    },
    {
      key: "fin_followups",
      title: "Follow-Ups From Last Call",
      type: "entry",
      hint: "close the loop — accountability is the point",
    },
    {
      key: "fin_wins",
      title: "Wins to Recognize",
      type: "note",
      hint: "",
    },
    {
      key: "fin_issues",
      title: "Issues / Risks to Raise",
      type: "note",
      hint: "",
    },
    {
      key: "fin_decisions",
      title: "Decisions Needed Today",
      type: "note",
      hint: "",
    },
    {
      key: "fin_coaching",
      title: "Coaching Focus",
      type: "note",
      hint: "",
    },
    {
      key: "fin_risk",
      title: "Risk & Watch List",
      type: "entry",
      hint: "not a fire yet — catch it before it is",
    },
    {
      key: "fin_actions",
      title: "Action Items Out of This Call",
      type: "entry",
      hint: "these flow into the shared Action Plan",
      toPlan: true,
    },
    {
      key: "fin_escalate",
      title: "Escalate to VP of Operations",
      type: "note",
      hint: "surface early — bad news travels up or it festers",
    },
  ],
};

export const BASE_VISIT_FORM: VisitForm = {
  key: "location_review",
  name: "Location review",
  sections: [
    {
      key: "standards",
      title: "Standards",
      group: "Walk-through",
      items: [
        "Entrance and public areas are clean and orderly",
        "Signage and required postings are current",
        "Safety equipment is in place and inspected",
        "Work areas are tidy and free of hazards",
      ],
    },
    {
      key: "people",
      title: "People",
      group: "Walk-through",
      items: [
        "Team members follow the dress and badge standard",
        "Required training is current",
        "Rotas cover the planned workload",
        "Recognition is visible and current",
      ],
    },
    {
      key: "operations",
      title: "Operations",
      group: "Operations review",
      items: [
        "Open work orders are tracked and closing on time",
        "Customer complaints are logged and followed up",
        "Last visit's action items are closed or on track",
      ],
    },
  ],
  financial: [
    {
      key: "fin_numbers",
      title: "The numbers",
      type: "metrics",
      hint: "know normal so the abnormal jumps out",
      rows: ["Revenue vs. budget", "Labour hours and overtime", "Open work orders"],
    },
    {
      key: "fin_actions",
      title: "Action items out of this call",
      type: "entry",
      hint: "these flow into the shared action plan",
      toPlan: true,
    },
    {
      key: "fin_risks",
      title: "Issues and risks to raise",
      type: "note",
      hint: "",
    },
  ],
};
